/**
 * useDemoResult — manages the result panel state.
 *
 * Single responsibility: hold the current demo result and provide
 * a way to set or clear it.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { useState, useCallback } from "react";
import type { DemoResult } from "../types.js";

export interface DemoResultState {
  readonly result: DemoResult | null;
  readonly setResult: (result: DemoResult) => void;
  readonly clearResult: () => void;
}

/**
 * Manages the current demo action result.
 *
 * Typically paired with useDemoMode — clear the result when mode changes.
 */
export function useDemoResult(): DemoResultState {
  const [result, setResultState] = useState<DemoResult | null>(null);

  return {
    result,
    setResult: useCallback((r: DemoResult): void => {
      setResultState(r);
    }, []),
    clearResult: useCallback((): void => {
      setResultState(null);
    }, []),
  };
}
